import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { Observable } from 'rxjs';
import { MaxisChartModalComponent } from "../maxis-chart-modal/maxis-chart-modal.component";
import { BoxPlotData, CellStyle, Chart, ChartConfig, ChartData, ChartTemplate, ChartType, ChartUtil, ColumnDefinitions, FigureIcon, GaugeData, HeaderStyle, KendoColumn, Row, SeriesType, TelerikData, TextData } from '../models';

@Component({
  selector: 'max-chart',
  templateUrl: './maxis-chart.component.html',
  styleUrls: ['./maxis-chart.component.scss']
})
export class MaxisChartComponent implements OnInit, OnChanges {
  ChartType = ChartType;

  @Input('actions') actions: { display: string, action: string }[];
  @Input() rangeColor: string = "#000000";
  @Output('action') actionEmitter = new EventEmitter();
  @Input() hideOwnerInfo: boolean = false;
  @Input() hideChartId: boolean = false;
  @Input() hideUpdatedInfo: boolean = false;
  @Input('chart') initchart: Chart;
  @Input() owner: any;
  @Input() gridHeight = 400;
  @Input() gridWidth = 390;
  @Input() hideFilters = false;
  @Input() titleFontSize = 24;
  @Input() descFontSize = 16;
  @Input() gridColumnWidth = 100;
  @Input() gridFontSize = 16;
  @Input() shortenCategories = false;
  @Input() hideLegend = false;
  @Input() islightTheme: boolean = false;
  @Input() dialogOnClick = false;
  @Input() dialogHeight = 540;
  @Input() dialogWidth = 500;
  @Input() showValuesOnSeries = false;

  data: Observable<any> | any;
  config: ChartConfig;
  chart: TelerikData;
  isTextCard: boolean = false;
  isGrid: boolean = false;
  isArcGauge: boolean = false;
  isRadialGauge: boolean = false;
  isBoxChart: boolean = false;

  labelConfig = {
    visible: true, rotation: 45, content: (options) => {
      if (options.value) {
        if (this.shortenCategories && options.value.length > 16) return options.value.substring(0, 16) + "...";
        else return options.value;
      } else if (options.text) {
        if (this.shortenCategories && options.text.length > 16) return options.text.substring(0, 16) + "...";
        else return options.text;
      }
    }
  }

  chartError: boolean = false;
  pieType: boolean = false;
  cartesianType: boolean = true;
  selectedFilter: any;
  loaded = false;
  isSafari: boolean = false;
  //Grid Card Stuff
  //gridRows is the actual data fed into the Kendo Grid (flattened out by row)
  public gridRows: Array<Object> = new Array<Object>();
  //Columns simplified as needed by Kendo Grid
  public gridColumns: Array<KendoColumn> = new Array<KendoColumn>();
  ownerInfo: any;
  // Gauge Card Stuff
  rangeSize: number = 30;

  gauge: GaugeData = new GaugeData();

  textData: TextData = new TextData();

  boxPlot: BoxPlotData[] = new Array<BoxPlotData>();

  constructor(public dialog: MatDialog) { }

  ngOnInit() {
    this.load();
  }

  load() {
    this.isTextCard = this.isArcGauge = this.isRadialGauge = this.isGrid = this.isBoxChart = false;
    this.generateChart();
    if (this.islightTheme) this.rangeColor = "#e6e5e5";
    switch (this.config.type) {
      case ChartType.text: {
        this.isTextCard = true;
        break;
      }
      case ChartType.arcGauge: {
        this.isArcGauge = true;
        break;
      }
      case ChartType.radialGauge: {
        this.isRadialGauge = true;
        break;
      }
      case ChartType.grid: {
        this.isGrid = true;
        break;
      }
      case ChartType.boxPlot: {
        this.isBoxChart = true;
        break;
      }
    }
    //identify if browser is safari
    const uA = navigator.userAgent;
    const vendor = navigator.vendor;
    if (/Safari/i.test(uA) && /Apple Computer/.test(vendor)) {
      this.isSafari = true;
    }
    this.loadCard();
    this.getOwnerInfo();
  }

  generateChart() {
    if (this.owner && !this.hideOwnerInfo) {
      this.initchart.owner = this.owner;
    }
    let config = new ChartConfig();
    config.tags = this.initchart.tags;
    config.name = this.initchart.name;
    config.guid = this.initchart.guid;
    config.ispublished = this.initchart.isPublished;
    config.description = this.initchart.description;
    config.template = this.initchart.template;
    config.type = this.initchart.chartType;
    config.createdBy = this.initchart.createdBy;
    config.createdDate = this.initchart.createdDate;
    config.updatedBy = this.initchart.updatedBy;
    config.updatedDate = this.initchart.updatedDate;
    config.hasAccess = this.initchart.hasAccess;
    config.dataUpdatedDate = new Date(Date.now());
    config.hasAccess = this.initchart.hasAccess;
    config.owner = this.initchart.owner;
    if (this.initchart.systemUpdatedDate) {
      config.systemUpdatedDate = new Date(this.initchart.systemUpdatedDate) > new Date(this.initchart.updatedDate) ? this.initchart.systemUpdatedDate : this.initchart.updatedDate;
    }
    this.config = config;

    if (this.initchart.chartData && !this.initchart.data) {
      this.initchart.data = new ChartData(this.initchart.chartData.data);
    }

    if (this.initchart.data) {
      this.data = this.initchart.data;
    }

    if (this.data && this.config) {
      // create a new ChartTemplate object in order for palettes to work when none is present
      if (!this.config.template) {
        this.config.template = new ChartTemplate(this.config.template);
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.load();
  }

  get DefaultPalette(): Array<string> {
    return ChartUtil.DefaultPalette;
  }

  rowCallback(context: RowClassArgs) {
    const isEven = context.index % 2 == 0;
    return {
      even: isEven,
      odd: !isEven
    };
  }

  loadCard(selFilter: any = null) {
    if (this.data) {
      // if (this.isGrid) {
      //   //for a newly created chart, chartType is always chart, 
      //   //so need to do this for preview to work for grid
      //   this.config.type = ChartType.grid
      // } else if (this.isArcGauge) {
      //   this.config.type = ChartType.arcGauge;
      // } else if (this.isRadialGauge) {
      //   this.config.type = ChartType.radialGauge;
      // } else if(this.isTextCard) {
      //   this.config.type = ChartType.text;
      // } else {
      //   this.config.type = ChartType.chart;
      // }
      if (selFilter == null && this.data.filteredData) {
        this.selectedFilter = this.data.filteredData[0];
      } else {
        this.selectedFilter = selFilter;
      }
      if (this.config) {
        switch (this.config.type) {
          case ChartType.chart:
            this.loadChart();
            break;
          case ChartType.grid:
            this.loadGrid();
            break;
          case ChartType.arcGauge:
            this.loadGauge();
            break;
          case ChartType.radialGauge:
            this.loadGauge();
            break;
          case ChartType.text:
            this.loadTextData();
            break;
          case ChartType.boxPlot:
            this.loadBoxPlot();
            break;
          default:
            break;
        }
      }
      this.loaded = true;
    }
    this.labelConfig.visible = !this.config.hideLabels
  }

  public loadChart() {
    if (this.data.chartType == SeriesType.donut || this.data.chartType == SeriesType.pie) {
      this.pieType = true;
      this.cartesianType = false;
    }
    try {
      this.chart = ChartUtil.getTelerikChart(this.config.template, this.selectedFilter ? this.selectedFilter : this.data.filteredData[0]);
      this.loaded = true;
      this.chartError = false;
    } catch (error) {
      console.error(error, this.config.template);
      this.chartError = true;
    }
  }

  public loadGrid() {
    try {
      let rows: Row[];
      let colDefs: ColumnDefinitions[];
      this.gridRows = new Array<Object>();
      this.gridColumns = new Array<KendoColumn>();

      if (this.selectedFilter && this.selectedFilter.grid) {
        rows = this.selectedFilter.grid.rows;
        colDefs = this.selectedFilter.grid.columnDefinitions;

        for (let r = 0; r < rows.length; r++) {
          let flatRow = new Object();
          for (let c = 0; c < rows[r].cells.length; c++) {
            let colName = this.getColumnName(c);
            flatRow[colName] = new Object();
            flatRow[colName]["value"] = rows[r].cells[c].value;
            var style = new CellStyle(rows[r].cells[c].style);
            flatRow[colName]["style"] = style.toNgStyle();
          }
          this.gridRows.push(flatRow);
        }

        for (let c = 0; c < colDefs.length; c++) {
          let col = new KendoColumn();
          col.title = colDefs[c].headerText;
          col.field = this.getColumnName(c);
          var colStyle = new HeaderStyle(colDefs[c].headerStyle);
          col.headerStyle = colStyle.toNgStyle();
          this.gridColumns.push(col);
        }
      }
      this.chartError = false;
    } catch (error) {
      console.error(error, this.data);
      this.chartError = true;
    }
  }

  public loadBoxPlot() {
    try {
      this.boxPlot = new Array<BoxPlotData>();
      if (this.selectedFilter && this.selectedFilter.boxPlot) {
        this.boxPlot = this.selectedFilter.boxPlot;
      }
      this.chartError = false;
    }
    catch (error) {
      console.error(error, this.data);
      this.chartError = true;
    }

  }

  public loadGauge() {
    try {
      this.gauge = new GaugeData();
      if (this.selectedFilter && this.selectedFilter.gauge) {
        this.gauge = new GaugeData(this.selectedFilter.gauge);
      }
      this.chartError = false;
    }
    catch (error) {
      console.error(error, this.data);
      this.chartError = true;
    }
  }

  public loadTextData() {
    try {
      this.textData = new TextData();
      if (this.selectedFilter && this.selectedFilter.textData) {
        this.textData = new TextData(this.selectedFilter.textData);
      }
      this.chartError = false;
    }
    catch (error) {
      console.error(error, this.data);
      this.chartError = true;
    }
  }

  getOwnerInfo() {
    if (this.config && this.config.owner && this.config.owner.user) {
      this.ownerInfo = this.config.owner.user;
      let status = "";
      if (this.config.ispublished) {
        status = "Published";
      } else {
        status = "Staged";
      }
      var result = "Chart Owner: " + this.ownerInfo.name + '\n' +
        "Chart Status: " + status;
      return result;
    }
  }

  geticonString(icon: FigureIcon): string {
    switch (icon) {
      case FigureIcon.trendingFlat:
        return "trending_flat";
      case FigureIcon.trendingUp:
        return "trending_up";
      case FigureIcon.trendingDown:
        return "trending_down";
      default:
        return null;
    }
  }

  getColumnName(columnIndex) {
    return 'C' + columnIndex;
  }

  actionClick(action) {
    this.actionEmitter.emit(action.action);
  }

  openDialog() {
    this.dialog.open(MaxisChartModalComponent, {
      data: {
        actions: this.actions,
        rangeColor: this.rangeColor,
        actionEmitter: this.actionEmitter,
        hideOwnerInfo: this.hideOwnerInfo,
        hideChartId: this.hideChartId,
        hideUpdatedInfo: this.hideUpdatedInfo,
        chart: this.initchart,
        owner: this.owner,
        gridHeight: this.gridHeight,
        gridWidth: this.gridWidth,
        hideFilters: this.hideFilters,
        titleFontSize: this.titleFontSize,
        descFontSize: this.descFontSize,
        gridColumnWidth: this.gridFontSize,
        gridFontSize: this.gridFontSize,
        shortenCategories: this.shortenCategories,
        hideLegend: this.hideLegend,
        dialogHeight: this.dialogHeight,
        dialogWidth: this.dialogWidth,
        islightTheme: this.islightTheme,
        showValuesOnSeries: this.showValuesOnSeries
      }
    })
  }
}